import { CHECK_STATUS } from '../types';

const withTimeout = async (promiseFactory, timeoutMs) => {
  let timer;
  try {
    return await Promise.race([
      promiseFactory(),
      new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error('Timed out')), timeoutMs);
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
};

const checkFetch = (url, timeoutMs, headers = {}) =>
  withTimeout(
    () => fetch(url, { method: 'HEAD', credentials: 'include', cache: 'no-store', headers }),
    timeoutMs
  ).then((response) =>
    response.ok || response.status < 400 ? CHECK_STATUS.PASS : CHECK_STATUS.FAIL
  );

// The Keycloak token endpoint answers 4xx to a plain request, so status can't gate the
// result. A resolved fetch proves the server is reachable; only a network/TLS block rejects.
const checkReachable = (url, timeoutMs) => {
  if (!url) return Promise.resolve(CHECK_STATUS.FAIL);
  return withTimeout(
    () => fetch(url, { method: 'GET', mode: 'no-cors', cache: 'no-store' }),
    timeoutMs
  )
    .then(() => CHECK_STATUS.PASS)
    .catch(() => CHECK_STATUS.FAIL);
};

const checkWebSocket = (url, timeoutMs) =>
  new Promise((resolve) => {
    if (!url || typeof WebSocket === 'undefined') {
      resolve(CHECK_STATUS.FAIL);
      return;
    }

    const socket = new WebSocket(url);
    const timer = setTimeout(() => {
      socket.close();
      resolve(CHECK_STATUS.FAIL);
    }, timeoutMs);

    socket.onopen = () => {
      clearTimeout(timer);
      socket.close();
      resolve(CHECK_STATUS.PASS);
    };
    socket.onerror = () => {
      clearTimeout(timer);
      socket.close();
      resolve(CHECK_STATUS.FAIL);
    };
  });

export const checkReachability = async ({ endpoints, timeoutMs }) => {
  // The API and Authentication checks both probe the Keycloak OIDC discovery endpoint.
  const authUrl = endpoints.oauthCheckUrl;

  const checks = await Promise.all([
    checkFetch(endpoints.portal, timeoutMs).catch(() => CHECK_STATUS.FAIL),
    checkReachable(authUrl, timeoutMs),
    checkWebSocket(endpoints.wss, timeoutMs),
    checkReachable(authUrl, timeoutMs),
  ]);

  return {
    portal: checks[0],
    api: checks[1],
    wss: checks[2],
    oauth: checks[3],
  };
};
